// Command shell — matches the doctor/patient portal chrome exactly: full-width dark top bar
// (pt-topbar) + cream sidebar nav (pt-sidebar) + off-canvas mobile drawer. Same shared chrome across
// all three portals (THU-354). Nav model (routes) is unchanged — presentation only; idle auto-logout
// and role label preserved.
import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { Brand, ThemeToggle, Modal, Sidebar, useNavCollapse } from "@championcare/ui";
import { useAppAuth } from "../auth";
import { roleLabel } from "../auth/probe";
import { useIdleLogout } from "../lib/useIdleLogout";

// 18px stroke icons (one inline SVG path per nav item).
const I = {
  overview: "M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z",
  members: "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M22 21v-2a4 4 0 0 0-3-3.87",
  doctors: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 8v8M8 12h8",
  applications:
    "M9 2h6a2 2 0 0 1 2 2H7a2 2 0 0 1 2-2zM7 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-1",
  visits: "M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z",
  payments: "M2 7h20v12a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1zM2 7l2-3h16l2 3M2 11h20",
  emr: "M21 12a9 9 0 1 1-3-6.7L21 8M21 3v5h-5",
  compliance: "M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6z",
  settings:
    "M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2zM15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0z",
  signout: "M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9",
} as const;

function Icon({ d }: { d: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
    </svg>
  );
}

type Item = { to: string; label: string; end?: boolean; icon: string };
const GROUPS: { label: string | null; items: Item[] }[] = [
  { label: "Home", items: [{ to: "/", label: "Overview", end: true, icon: I.overview }] },
  {
    label: "Members & doctors",
    items: [
      { to: "/members", label: "Members", icon: I.members },
      { to: "/doctors", label: "Doctors", icon: I.doctors },
      { to: "/applications", label: "Applications", icon: I.applications },
    ],
  },
  {
    label: "Operations",
    items: [
      { to: "/visits", label: "Visits", icon: I.visits },
      { to: "/payments", label: "Payments", icon: I.payments },
      { to: "/emr", label: "EMR Sync", icon: I.emr },
    ],
  },
  {
    label: "Compliance",
    items: [{ to: "/compliance", label: "Compliance", icon: I.compliance }],
  },
  {
    label: "Administration",
    items: [
      { to: "/team", label: "Team", icon: I.members },
      { to: "/settings", label: "Settings", icon: I.settings },
    ],
  },
];

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "CC";
}

export function Shell() {
  const { displayName, signOut, isSignedIn, role } = useAppAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const name = displayName ?? roleLabel(role);
  const [navOpen, setNavOpen] = useState(false);
  const [confirmOut, setConfirmOut] = useState(false);
  const { collapsed, setCollapsed, toggle } = useNavCollapse("cc-nav-collapsed-command");

  useIdleLogout(() => {
    void signOut();
    navigate("/sign-in");
  }, isSignedIn);

  useEffect(() => {
    document.body.classList.toggle("pt-nav-open", navOpen);
    return () => document.body.classList.remove("pt-nav-open");
  }, [navOpen]);

  const closeNav = () => setNavOpen(false);
  function handleSignOut() {
    closeNav();
    void signOut();
    navigate("/sign-in");
  }

  return (
    <div className="pt-root">
      <header className="pt-topbar">
        <Link to="/" className="pt-brand-link" aria-label="Champion Care — overview">
          <Brand onDark size={22} />
        </Link>
        <div className="pt-topbar__spacer" />
        <div className="pt-topbar__actions">
          <ThemeToggle />
          <span className="pt-userchip">
            <span className="pt-userchip__avatar">{initials(name)}</span>
            <span className="pt-userchip__name">{name}</span>
            <span className="pt-userchip__role">{roleLabel(role)}</span>
          </span>
          <button
            className="pt-navtoggle"
            onClick={() => setNavOpen((o) => !o)}
            aria-label="Menu"
            aria-expanded={navOpen}
          >
            <svg className="burger" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="7" x2="21" y2="7" /><line x1="3" y1="17" x2="21" y2="17" /></svg>
            <svg className="x" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="6" y1="6" x2="18" y2="18" /><line x1="6" y1="18" x2="18" y2="6" /></svg>
          </button>
        </div>
      </header>

      <div className={`pt-shell ${collapsed ? "nav-collapsed" : ""}`}>
        <div className="pt-nav-backdrop" onClick={closeNav} aria-hidden="true" />
        <Sidebar collapsed={collapsed} onToggle={toggle} onExpand={() => setCollapsed(false)}>
          {GROUPS.map((group, gi) => (
            <div key={gi} style={{ display: "contents" }}>
              {group.label && <div className="pt-navlabel">{group.label}</div>}
              {group.items.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  onClick={closeNav}
                  className={({ isActive }) => `pt-navlink ${isActive ? "is-active" : ""}`}
                >
                  <Icon d={n.icon} />
                  <span className="pt-navlink__label">{n.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
          <button className="pt-navlink pt-navlink--signout" onClick={() => setConfirmOut(true)}>
            <Icon d={I.signout} />
            <span className="pt-navlink__label">Sign out</span>
          </button>
        </Sidebar>

        <main className="pt-main">
          <div key={location.pathname} className="page-fade">
            <Outlet />
          </div>
        </main>
      </div>

      <Modal open={confirmOut} onClose={() => setConfirmOut(false)} labelledBy="signout-title">
        <div className="dialog-eyebrow">Sign out</div>
        <div className="dialog-title" id="signout-title">Sign out of <em>Champion Care?</em></div>
        <div className="dialog-body">You&rsquo;ll need to sign in again to access the command console.</div>
        <div className="dialog-actions">
          <button className="btn btn-ghost" onClick={() => setConfirmOut(false)}>Stay signed in</button>
          <button className="btn btn-primary" onClick={handleSignOut}>Sign out</button>
        </div>
      </Modal>
    </div>
  );
}
